// Equipo de «Arma la venta»: compacto, sin forma de quitarlo, con el reloj del apartado, el aviso
// de «Cambiar por el kit» y los lugares de Promociones.
import type { ReactNode } from 'react';
import { Box, ListTree, Repeat, Smartphone } from 'lucide-react';
import { Button } from '@/kit';
import { formatMXN } from '@/shared/lib/format/money';
import type { SaleSummary } from '../buildSale';
import { ReservationClock } from './ReservationClock';
import { variantLabel } from '@/shared/lib/format/capacity';

/** Kit que puede reemplazar al teléfono de la venta. */
export interface KitSwapOption {
    sku: string;
    name: string;
    priceCents: number;
    /** Si el kit alcanza con el plan del nivel; `undefined` sin nivel. */
    affordableOnCredit?: boolean;
    /** `false` si SureKeep no financia el kit. */
    financeable?: boolean;
    /** Componentes del kit, para «Ver qué incluye (N)». */
    componentCount?: number;
}

export function BuildSaleEquipment({
    equipment,
    onChange,
    kits = [],
    onSwapToKit,
    notice,
    firstToExpire,
    busy = false,
    onShowKitContents,
    children,
}: {
    equipment: NonNullable<SaleSummary['equipment']>;
    /** «Cambiar»; sin handler no se muestra. */
    onChange?: () => void;
    /** Kits que traen este teléfono; uno por botón «Cambiar por kit». */
    kits?: KitSwapOption[];
    onSwapToKit?: (sku: string) => void;
    /** Aviso previo al cambio por el kit, debajo de los botones. */
    notice?: ReactNode;
    /** Línea de la venta que vence primero; sin ella manda el vencimiento de la ancla. */
    firstToExpire?: { expiresAt: string; name?: string };
    busy?: boolean;
    /** Abre «Contenido del kit» del equipo o de un kit para cambiar; sin handler no se muestra. */
    onShowKitContents?: (sku: string) => void;
    /** Regalo, promo de venta y código, debajo del equipo. */
    children?: ReactNode;
}) {
    const { line, name, isKit, baseCents, netCents } = equipment;
    const detail = variantLabel(line.product ?? {});
    const Icon = isKit ? Box : Smartphone;
    const swapKits = onSwapToKit !== undefined ? kits : [];
    const swapLabel = (kit: KitSwapOption): string =>
        `Cambiar por ${swapKits.length > 1 ? kit.name : 'kit'} · ${formatMXN(kit.priceCents / 100)}`;
    const contentsButton = (sku: string, count: number, label: string) =>
        onShowKitContents !== undefined && (
            <Button
                key={`contents-${sku}`}
                size="sm"
                variant="ghost"
                disabled={busy}
                leftIcon={<ListTree aria-hidden />}
                aria-label={label}
                onClick={() => {
                    onShowKitContents(sku);
                }}
            >
                Ver qué incluye ({count})
            </Button>
        );

    return (
        <section
            aria-label="Equipo de la venta"
            className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4"
        >
            <div className="flex flex-wrap items-center gap-3">
                <span className="flex size-10 flex-none items-center justify-center rounded-md bg-muted text-muted-foreground">
                    <Icon className="size-5" aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        {isKit ? 'Kit' : 'Teléfono'} · apartado
                    </div>
                    <div className="truncate text-[14px] font-semibold">{name}</div>
                    {detail !== '' && (
                        <div className="text-[12px] text-muted-foreground">{detail}</div>
                    )}
                    <div className="text-[11px] text-muted-foreground">
                        <ReservationClock
                            expiresAt={firstToExpire?.expiresAt ?? line.expiresAt}
                            firstToExpireName={firstToExpire?.name}
                            phoneExpiresAt={line.expiresAt}
                            onReselect={onChange}
                        />
                    </div>
                </div>
                <div className="flex flex-col items-end tabular-nums">
                    {netCents < baseCents && (
                        <s className="text-[12px] text-muted-foreground">
                            {formatMXN(baseCents / 100)}
                        </s>
                    )}
                    <b className="text-[16px]">{formatMXN(netCents / 100)}</b>
                </div>
                {(onChange !== undefined ||
                    swapKits.length > 0 ||
                    (isKit && onShowKitContents !== undefined)) && (
                    <div className="flex w-full flex-wrap gap-2">
                        {onChange !== undefined && (
                            <Button size="sm" variant="outline" disabled={busy} onClick={onChange}>
                                Cambiar
                            </Button>
                        )}
                        {isKit &&
                            contentsButton(
                                line.sku,
                                line.components.length,
                                `Ver qué incluye ${name}`,
                            )}
                        {swapKits.map((kit) => [
                            <Button
                                key={kit.sku}
                                size="sm"
                                variant="outline"
                                disabled={busy}
                                leftIcon={<Repeat aria-hidden />}
                                onClick={() => {
                                    onSwapToKit?.(kit.sku);
                                }}
                            >
                                {swapLabel(kit)}
                            </Button>,
                            kit.componentCount !== undefined &&
                                contentsButton(
                                    kit.sku,
                                    kit.componentCount,
                                    `Ver qué incluye ${kit.name}`,
                                ),
                        ])}
                    </div>
                )}
            </div>
            {notice}
            {children}
        </section>
    );
}
